import Upload from "rc-upload";
import { useCallback, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Center,
  Flex,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spacer,
  Text,
  Tooltip,
  useMediaQuery,
} from "@chakra-ui/react";

import { download, Graph, toJsonBlob } from "@noli/core";
import { BoardStore, langStore } from "@noli/business";
import { HamburgerIcon } from "@chakra-ui/icons";

import { toast, useOwnToast } from "@/utils/misc";
import { Editor_Words } from "@/utils/lang/editor";
import { SafeOp } from "@/utils/safeOp";
import { genBlock } from "@/utils/bem";
import { sensitiveWordCheck } from "@/utils/string";
import { formatTime } from "@/utils/format-time";
import { Project_Words, useTranslate } from "@/utils/lang";

import IconButton from "@/components/IconButton";
import { Separator } from "@/modules/editor/components/common/Seperator";
import { InfoText } from "@/modules/editor/components/styleComponents/Texts";
import MenuOption from "../MenuOption";
import { ProjectListModal } from "../modals/ProjectListModal";
import { CreateProjectModal } from "../modals/CreateProjectModal";

import { ReactComponent as CloudSvg } from "@/assets/icons/cloud.svg";

import { cloudProjectActions } from "../../projectProcessor";
import { localCacheManager } from "../../localManager";
import { cloudProjectsManager } from "../../cloudManager";
import { DEFAULT_PROJECT_CONFIG } from "../../utils";
import { ConflictProcessModal } from "../modals/ConflictModal";
import "./style.scss";

const block = genBlock("c-project-manager");

const IProjectManager: React.FC = () => {
  const { currentProject } = cloudProjectsManager;
  const lang = langStore.tgt;
  const toastFn = useOwnToast();
  const { translate } = useTranslate();
  const [isSmallThan1200] = useMediaQuery(["(max-width: 1200px)"]);

  /* 下拉列表是否展示 */
  const [open, setOpen] = useState(false);
  /* 切换项目列表弹窗 */
  const [listModalOpen, setListModalOpen] = useState(false);
  /* 重命名相关  */
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(currentProject?.project_name ?? "");
  const nameInputRef = useRef<HTMLInputElement>(null);
  /* 新建项目 */
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [switchTriggerCreate, setSwitchTriggerCreate] = useState(false);

  const size = "32px";
  const hideMenu = () => setOpen(false);

  function handleImportLocal(file: Blob) {
    toast(toastFn, "success", translate(Editor_Words["importing-global-message"]));

    async function success(): Promise<void> {
      toast(toastFn, "success", translate(Editor_Words["import-global-success-message"]));
    }
    async function failed(): Promise<void> {
      toast(toastFn, "error", translate(Editor_Words["import-global-failed-message"]));
    }

    const reader = new FileReader();
    reader.onload = () =>
      SafeOp.importGraph(Graph.fromJson(reader.result as string), { success, failed });
    reader.readAsText(file);

    return {
      abort() {
        console.log("upload progress is aborted.");
      },
    };
  }

  async function handleDownload(): Promise<void> {
    const { currentProject } = cloudProjectsManager;
    toast(toastFn, "success", translate(Editor_Words["downloading-message"]));
    const blob = toJsonBlob(BoardStore.graph.toJson());
    await download(blob, `${currentProject?.project_name ?? "exported"}.noli`);
  }

  function handleSave() {
    cloudProjectActions.saveCurrentProject().then((success) => {
      console.log("saveLocalAndCloudProject : ", success);
      toast(
        toastFn,
        success ? "success" : "warning",
        translate(Editor_Words[`cloud-save-${success ? "success" : "failed"}-message`]),
      );
    });
  }

  function handleStartEditName() {
    setIsEditingName(true);
    const initValue = currentProject?.project_name ?? "";
    setEditName(initValue);
    setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.setSelectionRange(initValue.length, initValue.length);
    }, 20);
  }

  function handleCancelEditName() {
    setEditName(currentProject?.project_name ?? "");
    setIsEditingName(false);
  }

  function handleConfirmEditName() {
    if (currentProject?.project_name === editName) {
      setIsEditingName(false);
      return;
    }
    const id =
      currentProject?.project_id ||
      localCacheManager.data?.id ||
      DEFAULT_PROJECT_CONFIG.projectId();

    const newName = editName;
    sensitiveWordCheck(newName)
      .then((passed) => {
        if (passed) {
          cloudProjectActions
            .updateProjectName(id, newName)
            .then(() => {
              toastFn({
                title: translate(Editor_Words["cloud_project_rename_success_toast"]),
                status: "success",
              });
            })
            .catch((e) => {
              toastFn({
                title: `${e ?? translate(Editor_Words["cloud_project_rename_failed_toast"])}`,
                status: "error",
              });
              console.error("UpdateProjectName ERROR", e);
            })
            .finally(() => {
              setIsEditingName(false);
            });
        }
      })
      .catch(() => {
        toastFn({
          status: "error",
          onCloseComplete: () => {
            nameInputRef.current?.focus();
          },
          title: translate(Editor_Words["cloud_project_name_sensitive_toast"]),
        });
      });
  }

  function handleStartCreateProject() {
    setCreateModalOpen(true);
    setOpen(false);
  }

  const getFormatText = useCallback(
    (tooltip = false) => {
      if (cloudProjectsManager.lastSaveTime) {
        const pattern = tooltip
          ? translate(Project_Words.cloud_save_tooltip_with_time).replace("$", "YYYY-MM-DD HH:mm")
          : translate(Project_Words["cloud_save_info_with_time"]).replace("$", "HH:mm");
        return formatTime(cloudProjectsManager.lastSaveTime, pattern);
      }
      return "";
    },
    [cloudProjectsManager.lastSaveTime, lang],
  );

  const saveString = useMemo(() => {
    if (isSmallThan1200) {
      return undefined;
    }
    return cloudProjectsManager.saving ? translate(Project_Words["cloud_saving"]) : getFormatText();
  }, [cloudProjectsManager.saving, getFormatText, translate, isSmallThan1200]);

  return (
    <Flex color="text.icon" h={size} pos="relative" flex={1}>
      <IconButton
        w="fit-content"
        h={size}
        display="inline-block"
        fillbyCurrentColor={false}
        tip={getFormatText(true)}
        onClick={handleSave}
        icon={CloudSvg}
        style={{ display: "inline-block" }}
        className={block({ e: "cloud" })}
        text={saveString}
        wrapProps={{
          flexDir: "row",
          justifyContent: "flex-start",
        }}
        textProps={{
          whiteSpace: "nowrap",
          verticalAlign: "11px",
          w: "auto",
          display: "inline-block",
        }}
      />
      <Spacer />
      <Popover
        placement="bottom"
        isOpen={open}
        onClose={() => {
          setOpen(false);
        }}
        gutter={12}
      >
        <PopoverTrigger>
          <Center
            tabIndex={0}
            ml="12px"
            cursor="pointer"
            maxW="300px"
            flex={1}
            onClick={() => setOpen((open) => !open)}
          >
            <Tooltip
              hasArrow
              placement="bottom"
              bg="fill.icon"
              label={currentProject?.project_name ?? ""}
              isDisabled={!currentProject?.project_name}
            >
              <Text
                color="text.lightDisabled"
                whiteSpace="nowrap"
                maxW="160px"
                overflow="hidden"
                fontSize="14px"
                textOverflow="ellipsis"
              >
                {currentProject?.project_name}
              </Text>
            </Tooltip>
            <Center w={size} flexShrink={0}>
              <Tooltip
                hasArrow
                placement="bottom"
                bg="fill.icon"
                label={translate(Editor_Words["cloud_project_header_op_icon_tooltip"])}
              >
                <HamburgerIcon color="text.placeholder" />
              </Tooltip>
            </Center>
          </Center>
        </PopoverTrigger>
        <PopoverContent w="224px" border="none">
          <Flex
            w="224px"
            flexDir="column"
            alignItems="center"
            py="20px"
            bg="bg.panelBg"
            fontSize="12px"
            boxShadow="0px 0px 8px rgba(0, 0, 0, 0.25)"
          >
            <MenuOption
              hideMenu={hideMenu}
              text={translate(Editor_Words["cloud_project_switch"])}
              onClick={() => setListModalOpen(true)}
            />
            <MenuOption
              hideMenu={hideMenu}
              text={translate(Editor_Words["cloud_project_create"])}
              onClick={handleStartCreateProject}
            />
            <Separator w="80%" />
            <Upload
              className={block({ e: "upload" })}
              accept=".noli"
              customRequest={({ file }) => handleImportLocal(file as Blob)}
            >
              <MenuOption hideMenu={hideMenu} text={translate(Editor_Words["import-global"])} />
            </Upload>
            <MenuOption
              hideMenu={hideMenu}
              text={translate(Editor_Words["download-global"])}
              onClick={() => handleDownload()}
            />

            <Separator w="80%" />
            <MenuOption
              hideMenu={hideMenu}
              text={translate(Editor_Words["cloud_project_rename"])}
              onClick={handleStartEditName}
            />
          </Flex>
        </PopoverContent>
      </Popover>
      <Spacer />
      <ProjectListModal
        open={listModalOpen}
        onClose={() => setListModalOpen(false)}
        onDeleteClose={() => {
          setListModalOpen(true);
          setSwitchTriggerCreate(false);
        }}
        onCreate={() => {
          setListModalOpen(false);
          setCreateModalOpen(true);
          setSwitchTriggerCreate(true);
        }}
      />
      <CreateProjectModal
        open={createModalOpen}
        onClose={() => {
          if (switchTriggerCreate) {
            setListModalOpen(true);
          }
          setSwitchTriggerCreate(false);
          setCreateModalOpen(false);
        }}
      />
      <ConflictProcessModal />
      <Flex
        pos="absolute"
        zIndex={isEditingName ? 50 : -1}
        visibility={isEditingName ? "visible" : "hidden"}
        w="100%"
        bg="bg.panelBg"
        alignItems="center"
      >
        <Spacer />
        <InfoText whiteSpace="nowrap">
          {translate(Editor_Words["cloud_project_rename_label"])}：
        </InfoText>
        <Input
          ref={nameInputRef}
          flex={1}
          size="sm"
          value={editName}
          maxLength={20}
          minW="160px"
          focusBorderColor="text.secondary"
          borderColor="text.secondary"
          _focus={{}}
          onChange={(e) => setEditName(e.target.value)}
          onBlur={(e) => {
            console.log("onblur e: ", e);
            handleConfirmEditName();
          }}
          onKeyDown={(e) => {
            if (e.code === "Enter") {
              nameInputRef.current?.blur();
            }

            if (e.code === "Escape") {
              handleCancelEditName();
            }
          }}
        />
        <Spacer />
      </Flex>
    </Flex>
  );
};

export const ProjectManager = observer(IProjectManager);
